import React, { useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { Layout, ConfigProvider } from 'tea-component';
import LeftMenu from './NewIconMenu';
import '@/pages/intelligence/assets/scss/Base/common.scss';
import './index.scss';
import useLocaleClassName from '../pages/TopCharts/_hooks/useLocaleClassName';
import Loading from '@/pages/intelligence/components/Loading';
import useFollowingUnifiedIdModel, {
  jumpDbiDefaultPage,
} from '@/pages/intelligence/hooks/useHasFollowed';
import { useIntl, useDispatch, useSelector, useLocation, history, getLocale } from 'umi';
import { FILTER_NAMESPACE } from '@/pages/intelligence/models/filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { namespace as GUIDE_NAMESPACES, GUIDE_TYPES_INTELLIGENCE } from '@home/models/guide';
import { NEW_MARKS_NAMESPACE } from '@intelligence/models/newMarks';
import { getBasePath } from '@/utils/utils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import {
  MenuFooter,
  FooterLinkItem,
  METRICS_CREDITABILITY_URL,
  CREDITABILITY_NAME,
} from './MenuFooter';
import useNewMark from '@intelligence/hooks/useNewMark';
import creditability from '@intelligence/assets/svg/menu/creditability_new.svg';
import creditabilityBlue from '@intelligence/assets/svg/menu/creditability_blue_new.svg';
import { fetchLeftMenus } from '@/pages/intelligence/services/common';
import type { IMenuItemType, IMenuNewItemType } from '@/pages/intelligence/types/common';
import { MENUS_FOOTER_RED_MARK_MAPPING } from '@intelligence/_config/newMarks';
// import { getIPLibrary } from '@/pages/intelligence/utils/common';
import { has } from 'lodash-es';
import useSetQueryDefinition from '@intelligence/hooks/useQueryDefinition';
import { isEmpty } from '@intelligence/v3/utils/common';
import { condParams } from '@/pages/intelligence/_config/condParams';
import { formatMenus, generateMenusNewMarkCfg } from '@intelligence/utils/menu';
import { NewMarkDisplayMode, NewMarkType } from '@intelligence/enums/common';
import { DBIMenuType } from '@intelligence/enums/menu';
import { CONFIGURE_TEXTS_MODEL_NAMESPACE } from '@intelligence/models/configureTexts';
import { buriedPointPage } from '@home/utils/buriedPoint';
import { dbiThirdPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { useChunkLoadErrorHandler } from '@/pages/intelligence/hooks/useChunkLoadErrorHandler';
import { MenuClickContext, MenuListContext } from './MenuListContext';
import MenuHeaderWithSearch from './components/MenuHeaderWithSearch';

import type { IDBIMenuVO } from '@intelligence/types/menu';
import { useEmbeddedPageOptions } from '@home/hooks/useEmbeddedPageOptions';

const { Body, Content } = Layout;
export const ChartContext = React.createContext({} as any); // Context，增加ChartContext，提供chart-option值
const ChartProvider = ChartContext.Provider; // 提供者

// eslint-disable-next-line react/display-name
export default (props: any) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const dispatch = useDispatch();
  const { guideType } = useSelector((state: any) => state?.[GUIDE_NAMESPACES] || {});
  const isGuiding = GUIDE_TYPES_INTELLIGENCE?.includes(guideType);
  const [newMenuList, setNewMenuList] = useState<IDBIMenuVO[]>([]);
  const { newMarksState, addNewMarkRecord } = useNewMark();

  useChunkLoadErrorHandler();
  /**
   * 当前语言环境特有的className，用于 针对不同语言环境 需要做 特殊样式处理的场景
   * lang-en: 英文环境的className
   * lang-cn：中文环境的className
   */
  const localeClassName = useLocaleClassName();
  const { resetFilterExtend, resetDetailFilterExtend } = useFilterExtend({});
  // 游戏详情统一ChartOption
  const [statsChartOption, setStatsChartOption] = useState({});
  // 游戏详情统一ChartOption,是否重置legendselect
  const [ifResetLegend, setIfResetLegend] = useState(false);
  /** 情报进入系统时会根据URL判断是否是头部点击进来的，确认跳转到对应路由
   * 点击头部菜单跳转过来的链接为：/intelligence?fromHeaderClick=true
   */
  const searchParams = new URLSearchParams(window.location.search);

  // 除了fromHeaderClick=true之外，添加对情报定向缺失url的情况的处理，添加默认跳转路径
  // 后续添加/intelligence/ 之后非正确url，重定向跳转到默认路径的逻辑
  const { pathname } = useLocation();
  const embedOptions = useEmbeddedPageOptions();
  const isGameDetailsPage = pathname.toLowerCase().includes('/gamesdetails/');
  const showDetailPageHeader =
    embedOptions.showDetailPageHeader && isGameDetailsPage;
  const path = pathname.split('/');
  const preName = path[1];
  const aftName = path[2];
  const isNoPointUrl = preName === 'intelligence' && aftName === undefined;

  const fromHeaderClick = !!searchParams.get('fromHeaderClick') || isNoPointUrl;

  const { loading: followIdsLoading, firstFollowSegement } = useFollowingUnifiedIdModel({
    isgetData: fromHeaderClick,
  });

  // 初始化系统级 高频访问的 各种筛选器options数据，存在models里
  // useSystemOptionsEffect();

  // 获取公共配置
  // 初始化系统级 高频访问的 各种筛选器options数据，存在models里
  useSetQueryDefinition(condParams);

  useEffect(() => {
    if (!followIdsLoading) {
      /** 点击头部时判断如果有关注的游戏则跳转到Following,否则默认跳转到手游榜单 */
      jumpDbiDefaultPage(firstFollowSegement);
    }
  }, [firstFollowSegement, followIdsLoading]);

  /**
   * 获取菜单
   * @returns
   */
  const getMenuList = async () => {
    try {
      // 获取左侧菜单数据
      const menusRes = await fetchLeftMenus();
      // console.debug('111 menusRes: ', menusRes);
      // 转换左侧菜单数据
      const menus = formatMenus(menusRes?.data);
      // 生成左侧菜的New标签、Beta标签配置数据
      const menusNewMarkCfg = generateMenusNewMarkCfg(menus);
      // 获取New标签、Beta标签的访问记录数据，传入左侧菜单的New标签、Beta标签配置数据
      dispatch({
        type: `${NEW_MARKS_NAMESPACE}/fetchNewMarkRecords`,
        payload: menusNewMarkCfg || {},
      });
      // console.debug('111 menus: ', menus);
      setNewMenuList(menus);
    } catch (e) {
      console.log('111 error: ', e);
    } finally {
    }
  };

  useEffect(() => {
    dispatch({
      type: `${CONFIGURE_TEXTS_MODEL_NAMESPACE}/getConfigureTexts`,
      payload: {
        prefix: '',
      },
    });

    dispatch({
      type: `${FILTER_NAMESPACE}/getFilterSaveList`,
      payload: {},
    });

    // 获取新风格情报左侧菜单
    getMenuList();

    /** 清空继承值逻辑
     * 1 有继承值时，点击到首页或经分情况，再回到情报
     * 2 详情页Opinions跳转舆情再返回
     * */
    return () => {
      // 离开情报系统时，清空情报的继承值,搜索页除外
      const { pathname } = window.location; // 不要在外面取，会异步
      if (!(pathname.startsWith('/search') || pathname?.startsWith(`${getBasePath()}/search`))) {
        resetFilterExtend();
        resetDetailFilterExtend();
      }
    };
  }, []);

  const initIntelligenceExtend = () => {
    // 点击菜单，清空筛选器继承值
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveAllSizerInitFunc`,
      payload: true,
    });
    // 清空榜单游戏勾选继承值
    dispatch({
      type: `${DBI_TOPCHARTS}/setActiveUserGameList`,
      payload: [],
    });
    dispatch({
      type: `${DBI_TOPCHARTS}/setIfShowBaseReleaseTime`,
      payload: false,
    });
    dispatch({
      type: `${DBI_TOPCHARTS}/setSelectedTimeShortcutType`,
      payload: '',
    });
    // 清空从首页推荐跳转布尔值
    dispatch({
      type: `home/setIsFromRecommendHome`,
      payload: '',
    });
  };

  const clickMenu = (menu?: any) => {
    const { id, type, params } = menu || {};
    // console.debug('111 menus:menu ', menu);
    const { url } = params || {};
    if (type === DBIMenuType.ThirdPage && url) {
      const pageLogConfig = dbiThirdPageLogMes[id];
      if (pageLogConfig) {
        buriedPointPage(pageLogConfig);
      }
      window.open(url);
      return false;
    }
    initIntelligenceExtend();
  };

  const handleMenuSearchClick = (menu?: IDBIMenuVO) => {
    const menuId = menu?.id || '';

    if (isEmpty(menuId)) {
      return;
    }

    const menuNewMarkCfg = newMarksState?.[menuId];
    if (menuNewMarkCfg) {
      addNewMarkRecord?.({
        newMarkDisplayMode: menuNewMarkCfg?.newMarkDisplayMode ?? NewMarkDisplayMode.RecordControlled,
        newMarkType: menuNewMarkCfg?.newMarkType ?? NewMarkType.New,
        newMarkId: menuId,
      });
    }

    const clickResult = clickMenu(menu);
    if (clickResult === false) {
      return false;
    }

    history.push(`/${preName}/${menu?.path || ''}`);
  };

  const handleCreditabilityClick = () => {
    if (has(newMarksState, MENUS_FOOTER_RED_MARK_MAPPING[CREDITABILITY_NAME])) {
      addNewMarkRecord?.({
        newMarkDisplayMode: NewMarkDisplayMode.RecordControlled,
        // 这里固定为0，因为添加的时候也时固定0，这里不需要展示BETA、NEW，展示小红点，所以固定一个值就好
        newMarkType: NewMarkType.New,
        newMarkId: MENUS_FOOTER_RED_MARK_MAPPING[CREDITABILITY_NAME],
      });
    }
    history.push(METRICS_CREDITABILITY_URL);
  };

  return (
    <ConfigProvider locale={locale === 'en-US' ? 'en' : 'zh'}>
      <MenuListContext.Provider value={newMenuList}>
        <MenuClickContext.Provider value={handleMenuSearchClick}>
          <ChartProvider
            value={{ statsChartOption, setStatsChartOption, ifResetLegend, setIfResetLegend }}
          >
            <Body
              className={classnames('layout__body dbi-layout__body dbi-user-guide-root-node', {
                // 新手引导时，禁用用户交互
                'is-user-guiding': isGuiding,
                'dbh-embed-hide-page-header':
                  embedOptions.hidePageHeader && !showDetailPageHeader,
                'dbh-embed-hide-page-tabs':
                  embedOptions.hidePageTabs && !showDetailPageHeader,
              })}
            >
              {fromHeaderClick && followIdsLoading ? (
                <Loading />
              ) : (
                <>
                  {!embedOptions.hideLeftMenu && <LeftMenu
                    title={'Intelligence'}
                    titleCN="情报"
                    menuList={newMenuList}
                    clickMenu={clickMenu}
                    customHeaderElement={<MenuHeaderWithSearch />}
                    customFooter={<MenuFooter />}
                    customFooterShrink={
                      <div className="dbi-left-menu-footer-warp">
                        <FooterLinkItem
                          placement="top-start"
                          placementOffset={[-15, 20]}
                          hoverTip={formatMessage({ id: 'dbi_metrics_creditability_title' })}
                          isHighlight={pathname === METRICS_CREDITABILITY_URL}
                          imageSource={[creditability, creditabilityBlue]}
                          showMarkPoint={has(
                            newMarksState,
                            MENUS_FOOTER_RED_MARK_MAPPING[CREDITABILITY_NAME],
                          )}
                          onClick={handleCreditabilityClick}
                        />
                      </div>
                    }
                  />}
                  <Content className={classnames('dbi-body', localeClassName)}>
                    {props.children}
                  </Content>
                </>
              )}
            </Body>
          </ChartProvider>
        </MenuClickContext.Provider>
      </MenuListContext.Provider>
    </ConfigProvider>
  );
};
